// Workspaces API service - switches between mock and real API

import {
  Workspace,
  SaveWorkspaceRequest,
  UpdateWorkspaceRequest,
  LoadWorkspaceOptions,
  LoadWorkspaceResponse,
  ListWorkspacesParams,
  ListWorkspacesResponse,
  WorkspaceVersion,
  WorkspaceListItem,
  BackendWorkspace,
  BackendWorkspaceList,
  BackendWorkspaceWithVersion,
} from '@/types/workspace';
import { FailureRecord } from '@/types/failure';
import { WeibullAnalysisResult } from '@/utils/weibull';
import { mockWorkspacesApi } from './workspaces';
import { api } from './client';
import { isConfigEnabled } from '@/config/config';

/**
 * Main workspaces API service
 * Switches between mock (localStorage) and real API based on configuration
 */
export const workspacesApi = {
  /**
   * List user's workspaces
   */
  async list(params?: ListWorkspacesParams, userId?: string): Promise<ListWorkspacesResponse> {
    if (isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      return mockWorkspacesApi.list(params, userId);
    }

    // Backend expects query params for pagination and filtering
    const queryParams = {
      page: params?.page || 1,
      size: params?.size || 20,
      ...(params?.search && { search: params.search }),
      ...(params?.tags && { tags: params.tags.join(',') }),
      ...(params?.sortBy && { sortBy: params.sortBy }),
      ...(params?.sortOrder && { sortOrder: params.sortOrder }),
      ...(params?.workspaceType && { workspace_type: params.workspaceType }),
    };
    
    // Route to workspace-type-specific endpoint - default to weibull for backward compatibility
    const workspaceType = params?.workspaceType || 'weibull';
    const endpoint = `/workspaces/${workspaceType}`;
    const response = await api.get<BackendWorkspaceList>(endpoint, { params: queryParams });
    
    // Transform backend response to frontend format
    return {
      workspaces: response.workspaces.map(transformBackendWorkspaceToListItem),
      total: response.total_count,
      page: response.page,
      size: response.size,
    };
  },

  /**
   * Save a new workspace (always includes data and caches results)
   */
  async save(
    request: SaveWorkspaceRequest,
    currentFailures?: FailureRecord[] | unknown[],
    currentResults?: WeibullAnalysisResult | unknown,
    userId?: string
  ): Promise<Workspace> {
    if (isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      return mockWorkspacesApi.save(request, currentFailures as FailureRecord[], currentResults as WeibullAnalysisResult, userId);
    }

    // Construct payload matching backend NewWorkspace structure
    const payload: SaveWorkspaceRequest = {
      workspace_name: request.workspace_name,
      description: request.description,
      tags: request.tags,
      owner: request.owner, // Should be UUID
      workspace_type: request.workspace_type,
      configuration: request.configuration,
      current_failures: request.current_failures,
      current_results: request.current_results,
    };

    // Route to workspace-type-specific endpoint
    const workspaceType = request.workspace_type || 'weibull'; // Default to weibull for backward compatibility
    const response = await api.post<BackendWorkspaceWithVersion>(`/workspaces/${workspaceType}/new`, payload);
    return transformBackendWorkspaceWithVersionToWorkspace(response);
  },

  /**
   * Load a workspace by ID
   */
  async load(id: string, options?: LoadWorkspaceOptions, userId?: string, workspaceType?: 'weibull' | 'vbl'): Promise<LoadWorkspaceResponse> {
    if (isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      return mockWorkspacesApi.load(id, options, userId);
    }
    
    // Route to workspace-type-specific endpoint
    const type = workspaceType || 'weibull'; // Default to weibull for backward compatibility
    const response = await api.get<BackendWorkspaceWithVersion>(`/workspaces/${type}/${id}`, {
      params: options
    });
    
    return {
      workspace: transformBackendWorkspaceWithVersionToWorkspace(response),
      warnings: [],
    };
  },


  /**
   * Update an existing workspace
   */
  async update(
    id: string,
    request: UpdateWorkspaceRequest,
    currentFailures?: FailureRecord[] | unknown[],
    currentResults?: WeibullAnalysisResult | unknown,
    userId?: string,
    userName?: string
  ): Promise<Workspace> {
    if (isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      return mockWorkspacesApi.update(id, request, currentFailures as FailureRecord[], currentResults as WeibullAnalysisResult, userId, userName);
    }

    // Construct payload matching backend UpdatedWorkspace structure
    const payload: UpdateWorkspaceRequest = {
      workspace_id: request.workspace_id,
      workspace_name: request.workspace_name,
      description: request.description,
      tags: request.tags,
      workspace_type: request.workspace_type,
      configuration: request.configuration,
      current_failures: request.current_failures,
      current_results: request.current_results,
      updated_by: request.updated_by || userId, // Use current user if not provided
    };

    // Route to workspace-type-specific endpoint
    const workspaceType = request.workspace_type || 'weibull'; // Default to weibull for backward compatibility
    const response = await api.put<BackendWorkspaceWithVersion>(`/workspaces/${workspaceType}/update`, payload);
    return transformBackendWorkspaceWithVersionToWorkspace(response);
  },

  /**
   * Delete a workspace
   */
  async delete(id: string, userId?: string): Promise<void> {
    if (isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      return mockWorkspacesApi.delete(id, userId);
    }
    
    // Backend DELETE endpoint not implemented yet
    throw new Error('Delete workspace functionality is not yet available');
  },

  /**
   * Get version history for a workspace
   */
  async getVersions(id: string, userId?: string): Promise<WorkspaceVersion[]> {
    if (isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      return mockWorkspacesApi.getVersions(id, userId);
    }
    
    // Backend versions endpoint not implemented yet
    throw new Error('Workspace version history is not yet available');
  },

  /**
   * Restore a specific version of a workspace
   */
  async restoreVersion(id: string, versionNumber: number, userId?: string, userName?: string): Promise<Workspace> {
    if (isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      return mockWorkspacesApi.restoreVersion(id, versionNumber, userId, userName);
    }
    
    // Backend version restore endpoint not implemented yet
    throw new Error('Workspace version restore is not yet available');
  },
};

// Transformation functions to convert backend models to frontend types

function transformBackendWorkspaceToListItem(backendWorkspace: BackendWorkspace): WorkspaceListItem {
  return {
    id: backendWorkspace.workspace_id,
    name: backendWorkspace.workspace_name,
    description: backendWorkspace.description,
    version: 1, // Backend doesn't provide version number in list
    createdAt: backendWorkspace.created_at,
    updatedAt: backendWorkspace.updated_at,
    tags: backendWorkspace.tags || [],
    isLatestData: true, // Backend doesn't track this
    hasResults: backendWorkspace.failure_count > 0, // Infer from failure_count - workspaces with failures have been analyzed
    createdBy: {
      id: backendWorkspace.workspace_id, // Use workspace_id as fallback since backend doesn't provide user_id
      name: backendWorkspace.owner, // This is the formatted name from backend
    },
    metadata: {
      totalFailures: backendWorkspace.failure_count,
      dateRange: {
        from: backendWorkspace.date_from,
        to: backendWorkspace.date_to,
      },
      sitesIncluded: backendWorkspace.sites_included || [],
      componentsAnalyzed: [], // Backend doesn't provide this in list view
    },
  };
}

function transformBackendWorkspaceWithVersionToWorkspace(backendWorkspace: BackendWorkspaceWithVersion): Workspace {
  return {
    id: backendWorkspace.workspace_id,
    name: backendWorkspace.workspace_name,
    description: backendWorkspace.description,
    configuration: backendWorkspace.configuration,
    dataSnapshot: backendWorkspace.current_failures ? {
      failures: Array.isArray(backendWorkspace.current_failures) 
        ? backendWorkspace.current_failures
        : (backendWorkspace.current_failures.failures || []),
      capturedAt: backendWorkspace.workspace_updated_at,
      totalRecords: backendWorkspace.failure_count,
    } : undefined,
    results: backendWorkspace.current_results && Object.keys(backendWorkspace.current_results).length > 0 
      ? backendWorkspace.current_results as WeibullAnalysisResult 
      : undefined,
    metadata: {
      userId: backendWorkspace.workspace_id, // Backend doesn't provide user_id, use workspace_id as placeholder
      createdAt: backendWorkspace.workspace_created_at,
      updatedAt: backendWorkspace.workspace_updated_at,
      version: 1, // Backend doesn't track version numbers
      tags: backendWorkspace.tags || [],
      dataContext: {
        totalAvailableRecords: backendWorkspace.failure_count,
        dateRange: {
          from: backendWorkspace.date_from,
          to: backendWorkspace.date_to,
        },
        sitesIncluded: Array.isArray(backendWorkspace.sites_included) 
          ? backendWorkspace.sites_included 
          : (backendWorkspace.sites_included || []),
        componentsAnalyzed: [], // Backend doesn't provide this
      },
    },
    createdBy: {
      id: backendWorkspace.workspace_id, // Backend doesn't provide user_id
      name: backendWorkspace.version_created_by || backendWorkspace.owner,
    },
  };
}
